import { supabase } from '../lib/supabase';
import { isPlatformOwnerEmail } from '../utils/adminOverride';

export const COIN_PACKS = [
  {
    id: 'coin_pack_200',
    name: '$2 Coin Pack',
    coins: 200,
    price: 2,
    priceDisplay: '$2',
    description: '200 coins — ~30 min of gameplay',
    popular: false,
    color: '#6366F1',
  },
  {
    id: 'coin_pack_500',
    name: '$5 Coin Pack',
    coins: 500,
    price: 5,
    priceDisplay: '$5',
    description: '500 coins — ~1 week of extended play',
    popular: true,
    color: '#F59E0B',
  },
];

export const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    priceDisplay: '$0',
    period: 'forever',
    color: '#6B7280',
    icon: 'User',
    features: [
      'Up to 120s generation',
      '3 active projects',
      'Basic visual styles',
      'Community access',
    ],
    limits: { maxDuration: 120, projects: 3 },
    stripePriceId: null,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 12,
    priceDisplay: '$12',
    period: 'month',
    color: '#6366F1',
    icon: 'Zap',
    popular: true,
    features: [
      'Up to 5 min generation',
      'Unlimited projects',
      'All visual styles',
      'Priority generation',
      'HD exports',
    ],
    limits: { maxDuration: 300, projects: -1 },
    stripePriceId: 'pro_monthly',
  },
  {
    id: 'studio',
    name: 'Studio',
    price: 29,
    priceDisplay: '$29',
    period: 'month',
    color: '#F59E0B',
    icon: 'Star',
    features: [
      'Up to 10 min generation',
      'Unlimited projects',
      'Premium realistic styles',
      'Caption & lyric sync',
      'Team collaboration',
      '4K exports',
    ],
    limits: { maxDuration: 600, projects: -1 },
    stripePriceId: 'studio_monthly',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 99,
    priceDisplay: '$99',
    period: 'month',
    color: '#10B981',
    icon: 'Building2',
    features: [
      'Unlimited generation',
      'Custom duration policy',
      'Dedicated support',
      'API access',
      'White-label options',
      'SLA guarantee',
    ],
    limits: { maxDuration: Infinity, projects: -1 },
    stripePriceId: 'enterprise_monthly',
  },
];

export async function createCheckoutSession(productId) {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.access_token) {
      return { error: 'You must be signed in to make a purchase.' };
    }

    // ADMIN OVERRIDE: Platform owner never goes through Stripe
    if (isPlatformOwnerEmail(session?.user?.email)) {
      return { error: null, adminOverride: true, message: 'Platform owner has permanent access to all plans. No payment required.' };
    }

    const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
      body: { product_id: productId },
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });

    if (error) {
      return { error: error?.message || 'Failed to create checkout session.' };
    }

    return { url: data?.url, sessionId: data?.session_id };
  } catch (err) {
    console.error('createCheckoutSession error:', err);
    return { error: 'Something went wrong. Please try again.' };
  }
}

export async function getUserSubscription(userId) {
  try {
    // Check if this is the platform owner by looking up their email
    const { data: profile } = await supabase?.from('user_profiles')?.select('email, is_super_admin, billing_override')?.eq('id', userId)?.single();

    // ADMIN OVERRIDE: Always return enterprise for platform owner
    if (profile?.billing_override === true || isPlatformOwnerEmail(profile?.email)) {
      return { tier: 'enterprise', status: 'active', is_admin_override: true };
    }

    const { data, error } = await supabase?.from('user_subscriptions')?.select('*')?.eq('user_id', userId)?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('getUserSubscription error:', error);
      return null;
    }

    return data || { tier: 'free', status: 'active' };
  } catch (err) {
    console.error('getUserSubscription error:', err);
    return null;
  }
}

export async function getCoinBalance(userId) {
  try {
    const { data, error } = await supabase?.from('coin_balances')?.select('balance, lifetime_earned')?.eq('user_id', userId)?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('getCoinBalance error:', error);
      return { balance: 0, lifetimeEarned: 0 };
    }

    return {
      balance: data?.balance ?? 0,
      lifetimeEarned: data?.lifetime_earned ?? 0,
    };
  } catch (err) {
    console.error('getCoinBalance error:', err);
    return { balance: 0, lifetimeEarned: 0 };
  }
}

export async function getPurchaseHistory(userId) {
  try {
    const { data, error } = await supabase?.from('purchase_history')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(50);

    if (error) {
      console.error('getPurchaseHistory error:', error);
      return [];
    }

    return (data || [])?.map((p) => ({
      id: p?.id,
      type: p?.purchase_type,
      status: p?.purchase_status,
      amountCents: p?.amount_cents,
      currency: p?.currency,
      coinsGranted: p?.coins_granted,
      tierGranted: p?.tier_granted,
      description: p?.description,
      createdAt: p?.created_at,
    }));
  } catch (err) {
    console.error('getPurchaseHistory error:', err);
    return [];
  }
}
